import { useCallback, useMemo, useState } from 'react';

interface UndoRedoStateOptions {
  /** Max undo entries to retain; oldest are dropped past this. */
  maxHistoryEntries?: number;
}

interface CommitOptions {
  /**
   * When true (default), pushes the prior value onto the undo stack. Pass false
   * to coalesce a transient update (e.g. a mid-drag frame) into the previous
   * entry — the state still updates, but no new undo entry is created.
   */
  commit?: boolean;
}

export interface UndoRedoState<T> {
  present: T;
  setState: (updater: T | ((prev: T) => T), options?: CommitOptions) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** True once committed, and stays true after undoing back to the start. */
  hasChanges: boolean;
}

export const DEFAULT_MAX_HISTORY_ENTRIES = 50;

interface HistoryStacks<T> {
  past: T[];
  present: T;
  future: T[];
}

function resolveUpdater<T>(updater: T | ((prev: T) => T), prev: T): T {
  return typeof updater === 'function'
    ? (updater as (prev: T) => T)(prev)
    : updater;
}

/**
 * Generic undo/redo container over a past/present/future stack. Snapshots are
 * stored by reference, so callers must update state immutably (like
 * `useState`) for undo/redo to work.
 */
export function useUndoRedoState<T>(
  createInitialState: () => T,
  options?: UndoRedoStateOptions
): UndoRedoState<T> {
  const maxHistoryEntries =
    options?.maxHistoryEntries ?? DEFAULT_MAX_HISTORY_ENTRIES;

  const [stacks, setStacks] = useState<HistoryStacks<T>>(() => ({
    past: [],
    present: createInitialState(),
    future: [],
  }));

  const setState = useCallback<UndoRedoState<T>['setState']>(
    (updater, commitOptions) => {
      const commit = commitOptions?.commit ?? true;
      setStacks((current) => {
        const nextPresent = resolveUpdater(updater, current.present);
        if (nextPresent === current.present) {
          return current;
        }

        if (!commit) {
          return { ...current, present: nextPresent };
        }

        const past = [...current.past, current.present];
        if (past.length > maxHistoryEntries) {
          past.shift();
        }

        return { past, present: nextPresent, future: [] };
      });
    },
    [maxHistoryEntries]
  );

  const undo = useCallback(() => {
    setStacks((current) => {
      const previous = current.past[current.past.length - 1];
      if (previous === undefined) {
        return current;
      }

      return {
        past: current.past.slice(0, -1),
        present: previous,
        future: [current.present, ...current.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setStacks((current) => {
      const next = current.future[0];
      if (next === undefined) {
        return current;
      }

      return {
        past: [...current.past, current.present],
        present: next,
        future: current.future.slice(1),
      };
    });
  }, []);

  return useMemo(
    () => ({
      present: stacks.present,
      setState,
      undo,
      redo,
      canUndo: stacks.past.length > 0,
      canRedo: stacks.future.length > 0,
      hasChanges: stacks.past.length > 0 || stacks.future.length > 0,
    }),
    [stacks, setState, undo, redo]
  );
}
